import React, { useState, useCallback, useMemo } from 'react';

import { noop, isEqual } from 'lodash';

import DocumentSelectionSummary from './DocumentSelectionSummary';
import DocumentsTable, { ClaimDocumentMetadata } from './DocumentsTable';

import Button from '~/core/atoms/Buttons/Button';
import Tabs from '~/core/atoms/Tabs/Tabs';
import BaseDialog from '~/core/molecules/Dialog/BaseDialog';

export interface ChatContextDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedDocuments: ClaimDocumentMetadata[]) => void;
  documents: ClaimDocumentMetadata[];
  selected?: ClaimDocumentMetadata[];
}

const ChatContextDialog: React.FC<ChatContextDialogProps> = ({
  isOpen = false,
  onClose = noop,
  onSubmit = noop,
  documents = [],
  selected = [],
}) => {
  const [originalSelectedIds] = useState<string[]>(selected.map((doc) => doc.id).sort());
  const [internalSelected, setInternalSelected] = useState<ClaimDocumentMetadata[]>(selected);

  const hasChanges = useMemo(() => {
    const currentIds = internalSelected.map((doc) => doc.id).sort();
    return !isEqual(currentIds, originalSelectedIds);
  }, [originalSelectedIds, internalSelected]);

  const handleSubmit = useCallback(() => {
    onSubmit(internalSelected);
    onClose();
  }, [internalSelected, onSubmit, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const actions = (
    <div className="flex items-center justify-end gap-12">
      <Button outline size="sm" onClick={handleCancel}>
        Cancel
      </Button>
      <Button size="sm" onClick={handleSubmit} disabled={!hasChanges}>
        Add to Chat ({internalSelected.length})
      </Button>
    </div>
  );

  // Tab configuration
  const tabs = [
    {
      id: 'documents',
      label: 'Documents',
      content: (
        <div className="flex flex-col justify-center gap-24 pt-24">
          <DocumentSelectionSummary selectedDocuments={internalSelected} />

          <DocumentsTable
            documents={documents}
            isLoading={false}
            onSelectionChange={setInternalSelected}
            selectedDocuments={internalSelected}
            className="max-h-[50vh]"
          />
        </div>
      ),
    },
  ];

  // Dialog content with tabs
  const content = <Tabs tabs={tabs} defaultIndex={0} fullWidth={false} className="h-full" panelsClassName="pt-4" />;

  return (
    <BaseDialog isOpen={isOpen} onClose={handleCancel} title="Add context to chat" maxWidth="6xl" actions={actions}>
      {content}
    </BaseDialog>
  );
};

export default ChatContextDialog;
